/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable max-classes-per-file */

import CryptoJS from "crypto-js";
import i18next from "i18next";

import type { ResponseMainSseData } from "@/libs/client/HandleServerSendEventComponent/HandleServerSendEventInterface/responseSSEInterface";
import type { CurrencyEnum } from "@/types/utils/currency";
import { toLowerCaseString } from "@/utils/common/textHelper/stringConvertFormatUtills";

export enum PieceConceptEnum {
  P = "P",
  W = "W",
}

export interface RequestFlightFaresInterface {
  adult: number;

  child: number;

  infant: number;

  departureAirport: string;

  arrivalAirport: string;

  bookingClassDeparture: string;

  departureDate: string;

  returnDate?: string;

  bookingClassReturn?: string;

  thumbnail: number;

  errorCode?: string;
}

export class BaggageFee {
  baggageConcept: string;

  baggageKey: string;

  baggageType: string;

  baggageName: string;

  baggagePrice: number;

  baggagePiece: number;

  baggageLeg: string;

  baggageUnit: any;

  baggageTotalWeight: number;

  baggageCurrency: string;

  baggageIcon: string;

  constructor(data: any, isPieceConcept: boolean = false) {
    this.baggageKey = data.BaggageKey || "";
    this.baggageType = data.BaggageType || "";
    this.baggageName = data.BaggageName || "";
    this.baggagePrice = data.BaggagePrice || 0;
    this.baggagePiece = data.BaggagePiece || 0;
    this.baggageUnit = data.BaggageUnit || "";
    this.baggageTotalWeight = data.BaggageTotalWeight || 0;
    this.baggageCurrency = data.BaggageCurrency || "";
    this.baggageIcon = data.BaggageIcon || "";
    this.baggageLeg = data.BaggageLeg || "";
    this.baggageConcept = isPieceConcept
      ? PieceConceptEnum.P
      : PieceConceptEnum.W;
  }
}
export class MealFeeList {
  mealDetail: {
    seatMapLeg: string;
    departureAirport: string;
    arrivalAirport: string;
    departureDateTime: string;
    marketingAirline: string;
    flightNumber: string;
    meals: {
      mealKey: string;
      ssrCode: string;
      name: string;
      nameEn: string;
      price: number;
      imageURL: string;
      flightNumber: string;
    }[];
  };

  constructor(data: any) {
    this.mealDetail = {
      seatMapLeg: data?.mealDetail?.seatMapLeg || "",
      departureAirport: data?.mealDetail?.departureAirport || "",
      arrivalAirport: data?.mealDetail?.arrivalAirport || "",
      departureDateTime: data?.mealDetail?.departureDateTime || "",
      marketingAirline: data?.mealDetail?.marketingAirline || "",
      flightNumber: data?.mealDetail?.flightNumber || "",
      meals:
        data?.mealDetail?.meals?.map(
          (meal: MealsItem) => new MealsItem(meal),
        ) || [],
    };
  }
}
export class MealsItem {
  flightNumber: string;

  imageURL: string;

  mealKey: string;

  name: string;

  nameEn: string;

  price: number;

  ssrCode: string;

  constructor(data: any) {
    this.flightNumber = data?.FlightNumber || "";
    this.imageURL = data?.ImageURL || "";
    this.mealKey = data?.MealKey || "";
    this.name = data?.Name || "";
    this.nameEn = data?.NameEn || "";
    this.price = data?.Price || 0;
    this.ssrCode = data?.SSRCode || "";
  }
}

class FlexiblePolicy {
  chargeType: string;

  desc: string;

  flexible: string;

  maxChange: string;

  price: number;

  currency: string;

  constructor(data: any) {
    this.chargeType = data?.ChargeType || "";
    this.desc = data?.Desc || "";
    this.flexible = data?.Flexible || "";
    this.maxChange = data?.MaxChange || "";
    this.price = data?.Price || 0;
    this.currency = data?.Currency || "";
  }
}

class RefundPolicy {
  refundable: string;

  refundFee: number;

  refundDesc: string;

  currency: string;

  constructor(data: any) {
    this.refundable = data?.Refundable || "";
    this.refundFee = data?.RefundFee || 0;
    this.refundDesc = data?.RefundDesc || "";
    this.currency = data?.Currency || "";
  }
}
export enum PerkType {
  privilege = "privileges",
  freebie = "freebies",
}

export class FlightPerkBenefit {
  name: string;

  detail?: string;

  constructor(data: any) {
    this.name = data.name;
    this.detail = data?.detail || "";
  }
}

export class FlightPerks {
  campaign: string;

  airlines: string[];

  packages: string[];

  isCodeshare: boolean;

  isMultipleFlights: boolean;

  privileges: FlightPerkBenefit[];

  freebies: FlightPerkBenefit[];

  isShowPerk?: boolean;

  constructor(data: any) {
    this.campaign = data.campaign;

    this.airlines = data.airlines;

    this.packages = data.packages;

    this.isCodeshare = data.isCodeshare;

    this.isMultipleFlights = data.isMultipleFlights;

    this.privileges = data.campaign?.map(
      (item: FlightPerkBenefit) => new FlightPerkBenefit(item),
    );

    this.freebies = data.freebies?.map(
      (item: FlightPerkBenefit) => new FlightPerkBenefit(item),
    );

    this.isShowPerk = true;
  }
}

export class ResponseFlightPerks {
  code: string;

  data: FlightPerks[];

  message: string;

  status: boolean;

  constructor(data: any) {
    this.code = data.code;
    this.message = data.message;
    this.status = data.status;
    this.data = data.data;
  }
}

interface PackageInterface {
  name: string;

  fareBase: string;

  numberOfStops: string;

  seatAvailable: string;

  freeMeal: string;

  freeMealDesc: string;

  seatSelection: string;

  flexiblePolicy: FlexiblePolicy;

  deviceStreaming: string;

  wifi: string;

  powerOrUSBOutlets: string;

  refundPolicy: RefundPolicy;

  iGoPack: string;

  recordSet: string;

  lowCostAirline: string;

  adultPrice: number;

  adultFare: number;

  adultTax: number;

  childPrice: number;

  childFare: number;

  childTax: number;

  infantPrice: number;

  infantFare: number;

  infantTax: number;

  bookingClass: string;

  equivalentBookingClass: string;

  baggageFees: BaggageFee[];

  iGoPackKeys: any[];

  key: string;

  adultOriginalPrice: number;

  childOriginalPrice: number;

  infantOriginalPrice: number;
}

export class PerksBenefitPackage {
  detail: string;

  detail_en?: string;

  detail_th?: string;

  name: string;

  name_en?: string;

  name_th?: string;

  type: string;

  constructor(data: any) {
    this.detail = data.detail;
    this.detail_en = data.detail_en;
    this.detail_th = data.detail_th;
    this.name = data.name;
    this.name_en = data.name_en;
    this.name_th = data.name_th;
    this.type = data.type;
  }
}

export class PerksPackage {
  benefits: PerksBenefitPackage[];

  isPerks: boolean;

  perkIds: number[];

  constructor(data: any) {
    this.benefits = data?.benefits.map(
      (item: PerksBenefitPackage) => new PerksBenefitPackage(item),
    );
    this.isPerks = data?.isPerks;
    this.perkIds = data?.perkIds;
  }
}
export class Package {
  name: string;

  fareBase: string;

  numberOfStops: string;

  seatAvailable: string | null | undefined;

  freeMeal: string;

  freeMealDesc: string;

  seatSelection: string;

  flexiblePolicy: FlexiblePolicy;

  deviceStreaming: string;

  wifi: string;

  powerOrUSBOutlets: string;

  refundPolicy: RefundPolicy;

  iGoPack: string;

  recordSet: string;

  baggageFree: boolean;

  lowCostAirline: string;

  adultPrice: number;

  adultFare: number;

  adultTax: number;

  childPrice: number;

  childFare: number;

  childTax: number;

  infantPrice: number;

  infantFare: number;

  infantTax: number;

  bookingClass: string;

  equivalentBookingClass: string;

  baggageFees: BaggageFee[];

  iGoPackKeys: any[];

  key: string;

  adultOriginalPrice: number;

  childOriginalPrice: number;

  infantOriginalPrice: number;

  totalPrice: number;

  sumAdultPrice: number;

  sumChildPrice: number;

  sumInfantPrice: number;

  sumOriginalAdultPrice: number;

  sumOriginalChildPrice: number;

  sumOriginalInfantPrice: number;

  departureTimeFlightMinute: number;

  isGreenCertificate?: boolean;

  perks?: PerksPackage;

  runningNumber?: string;

  constructor(data: any) {
    this.name = data?.Name || "";
    this.fareBase = data?.FareBase || "";
    this.numberOfStops = data?.NumberOfStops || "";
    this.seatAvailable = data?.SeatAvailable || "";
    this.isGreenCertificate = data?.isGreenCertificate || false;
    this.freeMeal = data?.FreeMeal || "";
    this.freeMealDesc = data?.FreeMealDesc || "";
    this.seatSelection = data?.SeatSelection || "";
    this.flexiblePolicy = new FlexiblePolicy(data?.FlexiblePolicy);
    this.deviceStreaming = data?.DeviceStreaming || "";
    this.wifi = data?.Wifi || "";
    this.powerOrUSBOutlets = data?.PowerOrUSBOutlets || "";
    this.refundPolicy = new RefundPolicy(data?.RefundPolicy);
    this.iGoPack = data?.iGoPack || "";
    this.recordSet = data?.RecordSet || "";
    this.lowCostAirline = data?.LowCostAirline || "";
    this.baggageFree = toLowerCaseString(data?.BaggageFree) === "true" || false;
    this.adultPrice = data?.AdultPrice || 0;
    this.adultFare = data?.AdultFare || 0;
    this.adultTax = data?.AdultTax || 0;
    this.childPrice = data?.ChildPrice || 0;
    this.childFare = data?.ChildFare || 0;
    this.childTax = data?.ChildTax || 0;
    this.infantPrice = data?.InfantPrice || 0;
    this.infantFare = data?.InfantFare || 0;
    this.infantTax = data?.InfantTax || 0;
    this.bookingClass = data?.BookingClass || "";
    this.equivalentBookingClass = data?.EquivalentBookingClass || "";
    const baggageFees = data?.BaggageFees || [];
    const isSomeBagMoreThanOne = baggageFees.some(
      (bag: any) => (bag?.BaggagePiece || 0) > 1,
    );
    this.baggageFees = baggageFees?.map(
      (item: BaggageFee) => new BaggageFee(item, isSomeBagMoreThanOne),
    ) || [new BaggageFee({})];
    this.iGoPackKeys = data?.iGoPackKeys || [];
    this.key = data?.Key || "";
    this.adultOriginalPrice = data?.AdultOriginalPrice || 0;
    this.childOriginalPrice = data?.ChildOriginalPrice || 0;
    this.infantOriginalPrice = data?.InfantOriginalPrice || 0;
    this.totalPrice = data?.totalPrice || 0;
    this.sumAdultPrice = data?.sumAdultPrice || 0;
    this.sumChildPrice = data?.sumChildPrice || 0;
    this.sumInfantPrice = data?.sumInfantPrice || 0;
    this.sumOriginalAdultPrice = data?.sumOriginalAdultPrice || 0;
    this.sumOriginalChildPrice = data?.sumOriginalChildPrice || 0;
    this.sumOriginalInfantPrice = data?.sumOriginalInfantPrice || 0;
    this.departureTimeFlightMinute = 0;
    this.perks = new PerksPackage(data?.Perks);
    this.runningNumber = this.genRunningNumber?.(data?.Key);
  }

  genRunningNumber?(key: any): string {
    if (!key) return "";
    const hash = `${key.toString().substring(0, 2)}${CryptoJS.MD5(key).toString().substring(0, 8)}`;
    return hash;
  }
}

export interface GreenCertification {
  id: number;
  nameEn: string;
  nameTh: string;
}

export class Segment {
  departure: string;

  departureDateTime: string;

  arrival: string;

  arrivalDateTime: string;

  duration: string;

  operator: string;

  majorCarrierCode: string;

  carrierCode: string;

  codeShare: string;

  equipmentCode: string;

  EquipmentName: string;

  cabinClass: string;

  cabinClassName: string;

  flightNumber: string;

  travelTime: string;

  FlightTravelTime: number;

  FlightStopTime: number;

  mileage: number;

  techStop: string;

  techStopLocation: string;

  techStopArrivalDateTime: string;

  techStopDepartureDateTime: string;

  thumbnail: string;

  departureAirportName: string;

  departureAirportCityName: string;

  departureAirportCityNameEn: string;

  arrivalAirportName: string;

  arrivalAirportCityName: string;

  arrivalAirportCityNameEn: string;

  carrierName: string;

  carrierNameEn: string;

  techStopAirportName: string;

  techStopAirportCityName: string;

  techStopTotalTime: number;

  operatorName: string;

  isGreenCertificate?: boolean;

  greenCertifications: GreenCertification[] = [];

  arrivalCountryCode: string;

  departureCountryCode: string;

  constructor(data: any) {
    this.departure = data.Departure || "";
    this.departureDateTime = data.DepartureDateTime || "";
    this.arrival = data.Arrival || "";
    this.arrivalDateTime = data.ArrivalDateTime || "";
    this.duration = data.Duration || "";
    this.operator = data.Operator || "";
    this.majorCarrierCode = data.MajorCarrierCode || "";
    this.carrierCode = data.CarrierCode || "";
    this.codeShare = data.CodeShare || "";
    this.equipmentCode = data.EquipmentCode || "";
    this.EquipmentName =
      data.EquipmentName && data.EquipmentName?.toLowerCase() !== "n/a"
        ? data.EquipmentName
        : "";
    this.cabinClass = data.CabinClass || "";
    this.cabinClassName = data.CabinClassName || "";
    this.flightNumber = data.FlightNumber || "";
    this.travelTime = data.TravelTime || "";
    this.mileage = data.Mileage || 0;
    this.techStop = data.TechStop || "";
    this.techStopLocation = data.TechStopLocation || "";
    this.techStopArrivalDateTime = data.TechStopArrivalDateTime || "";
    this.techStopDepartureDateTime = data.TechStopDepartureDateTime || "";
    this.thumbnail = data.Thumbnail || "";
    this.departureAirportName = data.DepartureAirportName || "";
    this.departureAirportCityName = data.DepartureAirportCityName || "";
    this.departureAirportCityNameEn = data.DepartureAirportCityNameEn || "";
    this.arrivalAirportName = data.ArrivalAirportName || "";
    this.arrivalAirportCityName = data.ArrivalAirportCityName || "";
    this.arrivalAirportCityNameEn = data.ArrivalAirportCityNameEn || "";
    this.carrierName = data.CarrierName || "";
    this.carrierNameEn = data.CarrierNameEn || "";
    this.techStopAirportName = data.TechStopAirportName || "";
    this.techStopAirportCityName = data.TechStopAirportCityName || "";
    this.FlightTravelTime = data.FlightTravelTime || 0;
    this.FlightStopTime = data.FlightStopTime || 0;
    this.techStopTotalTime = data.TechStopTotalTime || 0;
    this.operatorName = data.OperatorName || data.Operator || "";
    this.arrivalCountryCode = data?.ArrivalCountryCode || "";
    this.departureCountryCode = data?.DepartureCountryCode || "";
    this.isGreenCertificate = data?.isGreenCertificate || false;
    this.greenCertifications = data?.greenCertifications || [];
  }
}
interface FareInterface {
  leg: string;

  currency: CurrencyEnum;

  departure: string;

  arrival: string;

  packages: Package[];

  segments: Segment[];

  flightCode?: string;

  flightTotalTime?: number;

  flightTotalStop?: number;

  timeUnit?: string;

  isDomestic: boolean;
}
export class Fare {
  leg: string;

  currency: CurrencyEnum;

  departure: string;

  arrival: string;

  packages: Package[];

  segments: Segment[];

  flightCode?: string;

  flightTotalTime?: number;

  flightTotalStop: number;

  isDomestic: boolean;

  timeUnit?: string;

  isRecommended?: boolean;

  isSpecialDeal?: boolean;

  showSpecialDeal?: boolean = false;

  perkCampaign: FlightPerks[];

  constructor(data: any) {
    this.leg = data.Leg || "";
    this.isDomestic = data.IsDomestic || false;
    this.currency = data.Currency || "";
    this.departure = data.Departure || "";
    this.arrival = data.Arrival || "";
    this.packages = data.Packages?.map(
      (item: Package) => new Package(item),
    ) || [new Package({})];
    this.segments = data.Segments?.map(
      (item: Segment) => new Segment(item),
    ) || [new Segment({})];
    this.flightCode = data.FlightCode || "";
    this.flightTotalTime = data.FlightTotalTime || 0;
    this.flightTotalStop = data.FlightTotalStop || 0;
    this.timeUnit = data.TimeUnit || "";
    this.isRecommended = data?.IsRecommended || false;
    this.isSpecialDeal = data?.IsSpecialDeal || false;
    this.perkCampaign = data?.perkCampaign || [];
  }
}

export class ResponseFlightFares {
  numberOfFares: number;

  typeOfFares: string;

  adult: string;

  child: string;

  infant: string;

  fares: Fare[];

  keySearch: string;

  keySearchDatetime: number;

  botURL: string;

  constructor(data: ResponseMainSseData, perksData?: FlightPerks[]) {
    this.numberOfFares = data.result.NumberOfFares || 0;
    this.typeOfFares = data.result.TypeOfFares || "";
    this.adult = data.result.Adult || "";
    this.child = data.result.Child || "";
    this.infant = data.result.Infant || "";
    this.fares = data.result?.Fares?.map((fare: Fare) => new Fare(fare)).map(
      (item: FareInterface) => {
        const packages = item.packages.map((packageOfFare: Package) => {
          const sumAdultPrice =
            packageOfFare.adultPrice * Number(data.result.Adult);
          const sumChildPrice =
            packageOfFare.childPrice * Number(data.result.Child);
          const sumInfantPrice =
            packageOfFare.infantPrice * Number(data.result.Infant);

          const sumOriginalAdultPrice =
            packageOfFare.adultOriginalPrice * Number(data.result.Adult);
          const sumOriginalChildPrice =
            packageOfFare.childOriginalPrice * Number(data.result.Child);
          const sumOriginalInfantPrice =
            packageOfFare.infantOriginalPrice * Number(data.result.Infant);

          const totalPrice = sumAdultPrice + sumChildPrice + sumInfantPrice;

          return {
            ...packageOfFare,
            totalPrice,
            sumAdultPrice,
            sumChildPrice,
            sumInfantPrice,
            sumOriginalAdultPrice,
            sumOriginalChildPrice,
            sumOriginalInfantPrice,
          };
        });

        const carrierCodeFare =
          item.segments[0]?.carrierCode && perksData
            ? item.segments[0]?.carrierCode
            : "";

        const perkCampaign = perksData?.filter((perk) =>
          perk.airlines.includes(carrierCodeFare),
        );

        const isAllCodeShare = item.segments.every(
          (segment) => toLowerCaseString(segment?.codeShare) === "true",
        );

        if (perkCampaign && perkCampaign?.length > 0) {
          perkCampaign.forEach((p) => {
            // eslint-disable-next-line no-param-reassign
            p.isShowPerk = true;
            if (!p?.isMultipleFlights && item.segments.length > 1) {
              // eslint-disable-next-line no-param-reassign
              p.isShowPerk = false;
            }
            if (!p?.isCodeshare && isAllCodeShare) {
              // eslint-disable-next-line no-param-reassign
              p.isShowPerk = false;
            }
          });
        }

        const newData = { ...item, packages, perkCampaign };
        return newData;
      },
    );

    this.keySearch = data.result.keySearch || 0;
    this.keySearchDatetime = data.result.keySearchDatetime || 0;
    this.botURL = data.result.botURL || "";
  }
}

export class ResponseFareUpSell {
  adult: number;

  child: number;

  fares: Fare[];

  infant: number;

  typeOfFares: string;

  constructor(data: any) {
    this.adult = data?.Adult || 0;
    this.child = data?.Child || 0;
    this.fares = data?.Fares?.map((segment: Fare) => new Fare(segment)).map(
      (item: FareInterface) => {
        const packages = item.packages.map((packageOfFare: Package) => {
          const sumAdultPrice = packageOfFare.adultPrice * Number(data?.Adult);
          const sumChildPrice = packageOfFare.childPrice * Number(data?.Child);
          const sumInfantPrice =
            packageOfFare.infantPrice * Number(data?.Infant);

          const sumOriginalAdultPrice =
            packageOfFare.adultOriginalPrice * Number(data?.Adult);
          const sumOriginalChildPrice =
            packageOfFare.childOriginalPrice * Number(data?.Child);
          const sumOriginalInfantPrice =
            packageOfFare.infantOriginalPrice * Number(data?.Infant);

          const totalPrice = sumAdultPrice + sumChildPrice + sumInfantPrice;

          return {
            ...packageOfFare,
            totalPrice,
            sumAdultPrice,
            sumChildPrice,
            sumInfantPrice,
            sumOriginalAdultPrice,
            sumOriginalChildPrice,
            sumOriginalInfantPrice,
          };
        });
        const newData = { ...item, packages };
        return newData;
      },
    );
    this.infant = data?.Infant || 0;
    this.typeOfFares = data?.TypeOfFares || 0;
  }
}

export class SportEquipmentFeesList {
  baggageKey: string = "";

  baggageType: string = "";

  baggageIcon: string = "";

  baggagePrice: number = 0;

  baggageCurrency: string = "";

  baggageConcept: string = "";

  baggageTotalWeight: number = 0;

  baggageUnit: string = "";

  baggagePiece: number = 0;

  baggageName: string = "";

  baggageLeg: string = "";

  baggageUnitByLang: string = "";

  constructor(data: any) {
    this.baggageKey = data.BaggageKey || "";
    this.baggageType = data.BaggageType || "";
    this.baggageIcon = data.BaggageIcon || "";
    this.baggagePrice = data.BaggagePrice || 0;
    this.baggageCurrency = data.BaggageCurrency || "";
    this.baggageConcept = data.BaggageConcept || "";
    this.baggageTotalWeight = data.BaggageTotalWeight || 0;
    this.baggageUnit = data.BaggageUnit || "";
    this.baggageUnitByLang =
      i18next.t(`flight:addon.${toLowerCaseString(data.BaggageUnit || "")}`) ||
      "";
    this.baggagePiece = data.BaggagePiece || 0;
    this.baggageName = data.BaggageName || "";
    this.baggageLeg = data.BaggageLeg || "";
  }
}
